import { BaseDto } from '../base.dto'
import { TranslationDto } from '../translation.dto'

/**
 * FR-05 — trạng thái Stamp Request.
 * Khớp enum `challenge_stamp_request_status` (`ChallengeStampRequest.status`).
 * `PENDING` là request khách vừa gửi — UI hiển thị nhãn "New" theo design.
 */
export const STAMP_REQUEST_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED'
} as const

export type StampRequestStatus = typeof STAMP_REQUEST_STATUS[keyof typeof STAMP_REQUEST_STATUS]

/**
 * Bảng `ChallengeStampRequestFile` — ảnh hoá đơn khách đính kèm (tối đa 5).
 *
 * ⚠️ OpenAPI live trả khoá **`evidence`** (không phải `files`) với shape
 * `[{id, fileId, sortOrder, mimeType}]` — `API Design.md` ghi sai thành
 * `files: [{fileId, fileName, url}]`.
 * `AdminStampRequestService.resolveEvidenceUrls()` chuyển sang DTO này.
 */
export class StampRequestFileDto {
  /** ✅ OpenAPI có trả `id` của bản ghi evidence. */
  public id?: string
  /** ✅ khớp BE. */
  public fileId?: string
  /**
   * ⚠️ BE-MISSING: `evidence[]` **không có** tên file
   * → lấy từ `FileService.findMultiple()`, fallback `evidence-N`.
   */
  public name?: string
  /**
   * ⚠️ BE-MISSING: `evidence[]` **không có** URL — chỉ có `fileId`.
   * → resolve qua `FileService.findMultiple()` (1 request batch cho cả bộ ảnh).
   * Thiếu bước này thì màn detail hiện toàn placeholder.
   */
  public url?: string
  /** ✅ OpenAPI **có** trả `sortOrder` (ghi chú BE-MISSING trước đây đã sai). */
  public sortOrder?: number
  /** ✅ OpenAPI trả thêm `mimeType` — chưa dùng ở UI. */
  public mimeType?: string

  public static create(data?: Partial<StampRequestFileDto>): StampRequestFileDto {
    const instance = new StampRequestFileDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Bảng `ChallengeStampRequest`.
 *
 * API #17 `GET /challenge-stamp-requests`      — list (15 field)
 * API #18 `GET /challenge-stamp-requests/:id`  — detail (= list + files + decidedByName + stampId)
 */
export class StampRequestDto extends BaseDto {
  /**
   * Mã hiển thị trên UI, ví dụ #AT26062F6S84 (cột "Request ID").
   *
   * ⚠️ BE-MISSING N5 (C37 vẫn mở): **không có** ở cả API #17 và #18
   * ⇒ `stamp-request-list.component.html:141` sẽ trống.
   */
  public requestCode?: string
  /**
   * FK `ChallengeCustomer`.
   * ⚠️ OpenAPI live **không trả** field này (chỉ có `customerId`) — khác `API Design.md`.
   * Luôn `undefined`; N1 coi như đã chốt là dùng `customerId`.
   */
  public challengeCustomerId?: string
  /**
   * ✅ ĐÃ CHỐT — API #17/#18 đều trả `challengeId` trong response.
   *
   * ⚠️ BE-MISSING B2: nhưng API #17 lại **không nhận `challengeId` làm query param**
   * ⇒ tab Stamp Request trong Challenge Detail không lọc được theo challenge đang xem.
   * Xem `AdminStampRequestService.list()`.
   */
  public challengeId?: string
  /** API #17/#18 — mới (string đã localize). */
  public challengeName?: string
  /** `ChallengeCustomer.customerId` — FK `User`. ✅ API #17/#18 đều trả. */
  public customerId?: string
  public customerName?: string
  /** ⚠️ BE trả tên `customerPhoneNumber`. */
  public phoneNumber?: string
  /** FK `ChallengeMilestone`. ⚠️ BE trả tên `milestoneId`. */
  public challengeMilestoneId?: string
  /** ⚠️ BE trả `milestoneName: string` (đã localize) — service bọc thành TranslationDto. */
  public milestoneName?: TranslationDto
  /**
   * `ChallengeMilestone.position` — FE dựng mã MS-00x từ giá trị này.
   * ⚠️ BE-MISSING: không có ở API #17/#18 — join từ API #10 (Challenge Detail).
   */
  public milestonePosition?: number
  /** `attemptNo` (> 0) — lần nộp thứ mấy cho cùng milestone. */
  public attemptNo?: number
  public status?: StampRequestStatus
  /** FR-05-BR-02 — `submittedAt`, thời điểm khách hàng nhấn Submit. ISO Date string. */
  public submittedAt?: string
  /** `decidedAt` — thời điểm Approve/Reject. ISO Date string. */
  public decidedAt?: string
  /**
   * FK `User`, tài khoản xử lý.
   * ⚠️ OpenAPI trả tên **`decidedById`** (không phải `decidedBy`) ở cả #18, #19, #20.
   */
  public decidedBy?: string
  /**
   * Tên tài khoản xử lý, BE join từ `User`.
   *
   * ✅ API #18 (detail) có trả `decidedByName`.
   * ⚠️ B8: OpenAPI khai response API #17 (list) là `any` nên **chưa xác minh được** list
   * có trả field này không; theo `API Design.md` thì không ⇒ cột "Processed by"
   * (`stamp-request-list.component.html:157`) có thể trống. Verify khi có data thật.
   * ⚠️ API #19/#20 không trả ⇒ sau khi xử lý điền tạm tên user hiện tại.
   */
  public decidedByName?: string
  /** `reason` — lý do từ chối (bắt buộc khi Reject) hoặc ghi chú xử lý. ✅ khớp BE. */
  public reason?: string
  /** FR-05-BR-02 — ảnh bằng chứng khách hàng tải lên. ✅ API #18 trả `files[]`. */
  public files?: StampRequestFileDto[]
  /**
   * Id của tem được phát sau khi Approve.
   * ✅ Có ở response API #19 (approve). ⚠️ API #20 (reject) **không trả** field này, và
   * OpenAPI của #18 (detail) cũng không khai báo.
   */
  public stampId?: string
  /**
   * ✅ OpenAPI live **có trả** `mallId` + `mallName` ở API #18 (khác `API Design.md`).
   * Actor scope vẫn do BE xử lý, nhưng #17 có thêm filter `mallId` nếu cần thu hẹp.
   */
  public mallId?: string
  /** ✅ OpenAPI #18 trả kèm (string đã localize). */
  public mallName?: string
  /**
   * API #17/#18 — `true` khi toàn bộ Reward Tier của Challenge không còn Reward Code
   * `NOT_CLAIMED`; `false` khi vẫn còn quà ở ít nhất một Tier.
   * `true` thì không cho Approve/Reject.
   */
  public isRewardOutOfStock?: boolean

  public static create(data?: Partial<StampRequestDto>): StampRequestDto {
    const instance = new StampRequestDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Option để lọc theo Milestone trong một Challenge (FR-05-BR-01).
 *
 * ⚠️ BE **không có** API `milestone-options`. Không cần bổ sung: API #10
 * (Challenge Detail) đã trả `milestones[{id, position, translations}]` — dựng option
 * trực tiếp từ đó. Xem `AdminStampRequestService.listMilestoneOptions()`.
 */
export class StampRequestMilestoneOptionDto {
  public id?: string
  /** `ChallengeMilestone.position` — FE dựng mã MS-00x từ giá trị này. */
  public position?: number
  public name?: TranslationDto

  public static create(data?: Partial<StampRequestMilestoneOptionDto>): StampRequestMilestoneOptionDto {
    const instance = new StampRequestMilestoneOptionDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-05-BR-01 — tham số tìm kiếm / lọc Stamp Request (API #17).
 * ✅ `search` / `milestoneId` / `milestoneName` / `status` / `limit` / `offset` khớp BE.
 */
export class FindStampRequestDto {
  /** Query `challengeId` — lọc theo Challenge đang xem, xem `AdminStampRequestService.list()`. */
  public challengeId?: string
  /** Tìm theo Customer name hoặc Phone number. ✅ khớp BE. */
  public search?: string
  public milestoneId?: string
  public milestoneName?: string
  /**
   * ✅ OpenAPI live có filter này (không có trong `API Design.md`).
   * Dùng để thu hẹp theo Mall khi actor scope trải nhiều Mall.
   */
  public mallId?: string
  public status?: StampRequestStatus
  /** ISO Date string → query `from`. */
  public fromDate?: string
  /** ISO Date string → query `to`. */
  public toDate?: string
  public page?: number
  public pageSize?: number

  public static create(data?: Partial<FindStampRequestDto>): FindStampRequestDto {
    const instance = new FindStampRequestDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

export class RejectStampRequestDto {
  /** Ghi vào `ChallengeStampRequest.reason`. */
  public reason: string

  public static create(data?: Partial<RejectStampRequestDto>): RejectStampRequestDto {
    const instance = new RejectStampRequestDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}
